<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import HelpSection from "../components/HelpSection.svelte";
	import Contact from "../components/Contact.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import Icon from "../components/Icon.svelte";
	import arrowLeft from "../../../core/assets/icons/filled-arrow-left.svg?raw";
	import BlogPosts from "../components/BlogPosts.svelte";
	import SimilarReferences from "../components/References/SimilarReferences.svelte";
	import type { ProductDetailProps } from "./ProductDetailProps.js";
	import ProductImageCarousel from "../components/ProductImageCarousel.svelte";
	import Content from "../components/Content.svelte";
	import Products from "../components/Products.svelte";
	import Link from "../components/Link.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getCategoryPath } from "../../../core/schema/paths/getCategoryPath";
	import { getTopLevelCategory } from "../../../core/schema/utils/getTopLevelCategory.js";
	import { getReferencesInProductCategoryPath } from "../../../core/schema/paths/getReferencesInProductCategoryPath.js";

	type $$Props = ProductDetailProps;

	const { product, otherProductsFromCategory, references, blogPosts, categories } = $$restProps as $$Props;

	const { STORAGE_URL, TENANT_LOCALE } = getGlobalSettings();
	setContext(thumbnailImageContextKey, `${STORAGE_URL}/${product.primaryImage.path}`);
	$: topLevelCategory = getTopLevelCategory(product.categoryId, categories);
</script>

<Layout>
	<button
		class="ml-10 mt-8 flex cursor-pointer gap-4"
		on:click={() => {
			const category = getTopLevelCategory(product.categoryId, categories);
			if (category) {
				window.location.href = `/${getCategoryPath(category)}`;
			}
		}}
	>
		<Icon icon={arrowLeft} class="text-gray h-4 w-4" />
		<span class="text-xs font-extrabold">Zpět</span>
	</button>
	<div class="mx-auto mt-12 px-4 text-center">
		<span
			class="font-quicksand text-primary m-0 text-center text-[1.375rem] font-bold uppercase leading-10 tracking-[0.3em] md:text-[2rem]"
		>
			Produkt
		</span>
	</div>

	<div
		class="mt-16 flex h-full flex-col items-center justify-center gap-12 px-4 sm:px-6 lg:flex-row lg:items-stretch lg:px-10"
	>
		<ProductImageCarousel thumbnailImage={product.primaryImage} photoGallery={product.images} />
		<div class="flex w-full max-w-[34.375rem] flex-col items-center gap-6 lg:items-start xl:w-[34.375rem]">
			<h1 class="text-bito-black font-fustat m-0 pt-0 text-center text-[2.25rem] font-bold lg:text-left">
				{nbspify(product.name)}
			</h1>
			<p class="text-gray-400">{nbspify(product.perex)}</p>
			{#if product.keyProperties.length > 0}
				<div class="self-start">
					<h3 class="text-bito-black text-lg">Klíčové vlastnosti</h3>
					<ul class="ml-4 list-disc text-gray-400">
						{#each product.keyProperties as keyProperty}
							<li>{nbspify(keyProperty.name)}{keyProperty.value ? ` ${nbspify(keyProperty.value)}` : ""}</li>
						{/each}
					</ul>
				</div>
			{/if}
			{#if product.specifications.length > 0}
				<div class="self-start">
					<h3 class="text-bito-black text-lg">Technické údaje</h3>
					<ul class="ml-4 list-disc text-gray-400">
						{#each product.specifications as specification}
							<li>{nbspify(specification.name)} {nbspify(specification.value)}</li>
						{/each}
					</ul>
				</div>
			{/if}
			{#if product.catalogue}
				<Link
					asButton
					href="{STORAGE_URL}/{product.catalogue.path}"
					target="_blank"
					class="w-[13.75rem] cursor-pointer lg:mt-auto"
					variant="primary"
				>
					{nbspify("Stáhnout katalog (.pdf)")}
				</Link>
			{/if}
		</div>
	</div>

	<div class="container mt-20 flex flex-col gap-20 lg:gap-32">
		<Content
			content={product.content}
			contentClass="ckeditor-content ckeditor-bito-text max-w-[41.875rem] mx-auto px-4 sm:px-6 lg:px-10"
		/>

		{#if references.length > 0}
			<div class="flex flex-col gap-4">
				<SimilarReferences similarReferences={references} title="Reference" />

				{#if topLevelCategory}
					<Link
						asButton
						variant="secondary"
						class="mx-auto w-full max-w-[27.5rem]"
						href="/{getReferencesInProductCategoryPath(topLevelCategory, TENANT_LOCALE)}"
					>
						{nbspify("Zobrazit další")}
					</Link>
				{/if}
			</div>
		{/if}
		{#if otherProductsFromCategory.length > 0}
			<Products products={otherProductsFromCategory} {categories} />
		{/if}
		<BlogPosts title="Doporučujeme" {blogPosts} />
	</div>
	<div class="mt-20 flex flex-col gap-20 lg:mt-32 lg:gap-32">
		<HelpSection />
		<Contact />
	</div>
</Layout>
